import React from 'react';
import { IToolManifest } from '../../../core/tools/interfaces/ITool';
import { ShapeCreationTool } from '../../../core/tools/standard/ShapeCreationTool';
import { Node, Sex } from '../../../core/models/scene';
import { ToolComponentFactory } from '../../../core/tools/components/ToolComponents';
import { Plus, Square, Circle, Diamond } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';
import i18n from '../../../i18n/config';

// Genogram-specific shape creation tool
export class GenogramShapeCreationTool extends ShapeCreationTool {
  manifest: IToolManifest;
  
  private addNodeHandler: ((node: Node) => void) | null = null;
  private deleteNodeHandler: ((id: string) => void) | null = null;
  
  constructor() {
    super();
    
    // Build manifest from base and add genogram-specific properties
    this.manifest = {
      ...this.getBaseManifest(),
      id: 'genogram-create',
      name: i18n.t('genogram:tools.addPerson', { defaultValue: 'Add Person' }) as string,
      icon: '➕',
      description: 'Add new person nodes to the genogram',
      supportedDiagramTypes: ['genogram'],
    } as IToolManifest;
    
    // Set default shape type
    this.pendingShapeType = 'male';
  }
  
  setNodeHandlers(
    addNode: (node: Node) => void,
    deleteNode: (id: string) => void
  ): void {
    this.addNodeHandler = addNode;
    this.deleteNodeHandler = deleteNode;
  }
  
  getShapeTypes(): { value: string; label: string; icon?: string }[] {
    return [
      { value: 'male', label: i18n.t('genogram:toolbar.addMale', { defaultValue: 'Add Male' }) as string, icon: '◻' },
      { value: 'female', label: i18n.t('genogram:toolbar.addFemale', { defaultValue: 'Add Female' }) as string, icon: '◯' },
      { value: 'unknown', label: i18n.t('genogram:toolbar.addUnknown', { defaultValue: 'Add Unknown' }) as string, icon: '◇' }
    ];
  }
  
  createShape(type: string, x: number, y: number): Node {
    const personLabel = (() => {
      if (type === 'female') return i18n.t('genogram:person.female', { defaultValue: 'Female' }) as string;
      if (type === 'male') return i18n.t('genogram:person.male', { defaultValue: 'Male' }) as string;
      return i18n.t('genogram:person.label', { defaultValue: 'Person' }) as string;
    })();

    return {
      id: `person-${Date.now()}`,
      type: 'person',
      name: personLabel,
      sex: type as Sex,
      x,
      y,
      w: 60,
      h: 60
    };
  }
  
  addShape(shape: any): void {
    if (this.addNodeHandler) {
      this.addNodeHandler(shape as Node);
    }
  }
  
  deleteShape(id: string): void {
    if (this.deleteNodeHandler) {
      this.deleteNodeHandler(id);
    }
  }
  
  getDefaultPosition(): { x: number; y: number } {
    // Get random position near center
    return {
      x: window.innerWidth / 2 + (Math.random() - 0.5) * 200,
      y: window.innerHeight / 2 + (Math.random() - 0.5) * 200
    };
  }
  
  // Use standardized toolbar component
  getToolbarComponent(): React.ComponentType<any> {
    return ({ onExecute }: any) => {
      const shapeTypes = this.getShapeTypes();
      
      const handleCreateShape = (type: string) => {
        this.pendingShapeType = type;
        onExecute({ type });
      };
      
      // Map shape type icons to Lucide icons
      const getShapeIcon = (type: string) => {
        switch (type) {
          case 'male': return Square;
          case 'female': return Circle;
          case 'unknown': return Diamond;
          default: return Plus;
        }
      };
      
      const { t } = useTranslation('genogram');
      return ToolComponentFactory.createSelect({
        icon: Plus,
        placeholder: t('toolbar.addPerson', 'Add person'),
        onValueChange: handleCreateShape,
        options: shapeTypes.map(type => ({
          value: type.value,
          label: type.label,
          icon: getShapeIcon(type.value),
          description: undefined
        }))
      });
    };
  }
}
